import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';

declare var Meteor: any;

export interface FileItem {
  id: string;
  name: string;
  path: string;
  type: 'file' | 'folder';
  mimeType?: string;
  size?: number;
  modified?: string;
  thumbnail?: string;
  icon?: string;
  children?: FileItem[];
}

@Injectable({
  providedIn: 'root'
})
export class FileManagerService {
  constructor() {}

  /**
   * List files and folders for a user at a given path
   */
  listFiles(userId: string, path: string = '/'): Observable<FileItem[]> {
    return from(
      new Promise<any>((resolve, reject) => {
        Meteor.call('listLocalFiles', userId, path, (error: any, result: any) => {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        });
      })
    ).pipe(
      map((result: any) => {
        if (result.code === 200 && result.data) {
          return result.data.map((item: any) => this.mapToFileItem(item));
        }
        return [];
      })
    );
  }

  /**
   * Upload a file
   */
  uploadFile(
    userId: string,
    fileName: string,
    fileContent: string,
    mimeType: string,
    parentPath: string = '/',
    thumbnail?: string
  ): Observable<any> {
    return from(
      new Promise<any>((resolve, reject) => {
        Meteor.call('uploadLocalFile', userId, fileName, fileContent, mimeType, parentPath, thumbnail, (error: any, result: any) => {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        });
      })
    );
  }

  /**
   * Create a folder
   */
  createFolder(userId: string, folderName: string, parentPath: string = '/'): Observable<any> {
    return from(
      new Promise<any>((resolve, reject) => {
        Meteor.call('createLocalFolder', userId, folderName, parentPath, (error: any, result: any) => {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        });
      })
    );
  }

  /**
   * Get file details
   */
  getFileDetails(userId: string, fileId: string): Observable<FileItem | null> {
    return from(
      new Promise<any>((resolve, reject) => {
        Meteor.call('getLocalFileDetails', userId, fileId, (error: any, result: any) => {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        });
      })
    ).pipe(
      map((result: any) => {
        if (result.code === 200 && result.data) {
          return this.mapToFileItem(result.data);
        }
        return null;
      })
    );
  }

  /**
   * Download a file
   */
  downloadFile(userId: string, fileId: string): Observable<any> {
    return from(
      new Promise<any>((resolve, reject) => {
        Meteor.call('downloadLocalFile', userId, fileId, (error: any, result: any) => {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        });
      })
    );
  }

  /**
   * Delete a file or folder
   */
  deleteFile(userId: string, fileId: string): Observable<boolean> {
    return from(
      new Promise<any>((resolve, reject) => {
        Meteor.call('deleteLocalFile', userId, fileId, (error: any, result: any) => {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        });
      })
    ).pipe(
      map((result: any) => result.code === 200)
    );
  }

  /**
   * Search files
   */
  searchFiles(userId: string, query: string): Observable<FileItem[]> {
    return from(
      new Promise<any>((resolve, reject) => {
        Meteor.call('searchLocalFiles', userId, query, (error: any, result: any) => {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        });
      })
    ).pipe(
      map((result: any) => {
        if (result.code === 200 && result.data) {
          return result.data.map((item: any) => this.mapToFileItem(item));
        }
        return [];
      })
    );
  }

  /**
   * Normalize path for consistent navigation (leading slash, no double slashes, no trailing slash for folders)
   */
  private normalizeItemPath(path: string | undefined): string {
    if (!path || path.trim() === '') return '/';
    const p = path.replace(/\/+/g, '/').replace(/\/$/, '').trim();
    return p === '' ? '/' : (p.startsWith('/') ? p : '/' + p);
  }

  /**
   * Map database file item to component file item
   */
  private mapToFileItem(item: any): FileItem {
    return {
      id: item._id || item.id,
      name: item.name ?? '',
      path: this.normalizeItemPath(item.path),
      type: item.type === 'folder' ? 'folder' : 'file',
      mimeType: item.mimeType,
      size: item.size,
      modified: item.modified ? new Date(item.modified).toISOString() : item.updatedAt ? new Date(item.updatedAt).toISOString() : undefined,
      thumbnail: item.thumbnail,
      icon: this.getFileIcon(item),
      children: item.children
    };
  }

  /**
   * Get file icon based on type and mimeType
   */
  private getFileIcon(item: any): string {
    if (item.type === 'folder') return 'fa-folder';
    if (!item.mimeType) return 'fa-file';
    
    const mimeType = item.mimeType.toLowerCase();
    if (mimeType.startsWith('image/')) return 'fa-image';
    if (mimeType.startsWith('video/')) return 'fa-video';
    if (mimeType.startsWith('audio/')) return 'fa-music';
    if (mimeType === 'application/pdf') return 'fa-file-pdf';
    if (mimeType.includes('word')) return 'fa-file-word';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'fa-file-excel';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'fa-file-powerpoint';
    if (mimeType.includes('zip') || mimeType.includes('archive')) return 'fa-file-archive';
    if (mimeType.includes('javascript') || mimeType.includes('json') || mimeType.includes('code')) return 'fa-file-code';
    if (mimeType.includes('text')) return 'fa-file-alt';
    
    return 'fa-file';
  }
}
